import { Checkbox, Loader } from "@mantine/core";
import { SearchX } from "lucide-react";
import type { ReactNode } from "react";

import type { Asset, SceneMatch } from "../../api/generated/model";
import { shapeStyle } from "../../platform/media/asset-shape";
import glyph from "../../ui/assets/image-card-placeholder.svg";
import { CardPicture } from "../../ui/CardPicture";
import { MediaCard, type MediaCardDrag } from "../../ui/MediaCard";
import { scrubTime, VideoScrubber } from "../../ui/VideoScrubber";
import classes from "./library.module.css";

/**
 * One asset in the library's masonry (design-system.md 3.5): the picture at its own shape (the
 * thumbnail, the original image), a video as live's scrubber (its poster or thumbnail, else its
 * own frame, and its length), and its actions in the hover pill; no kind badge, as live's. A click
 * on the picture opens the preview (live's). Its URLs are signed for about an hour (S1): they are rendered as the API returned them
 * and never stored; a refetch brings fresh ones.
 */
export function AssetCard({
  asset,
  actions,
  onOpen,
  selection,
  scene,
  indexed = false,
  fill = false,
  drag,
}: {
  asset: Asset;
  actions: ReactNode;
  onOpen?: (() => void) | undefined;
  /** Live's round checkbox at the top right, on hover (S1c); none in the trash. */
  selection?: { checked: boolean; onToggle: () => void } | undefined;
  /** A search hit's video scene (spec S3): the card rests on its frame and its time. */
  scene?: SceneMatch | null | undefined;
  /** Semantic search is on (spec S3): the card says when the file is being indexed or failed. */
  indexed?: boolean | undefined;
  /** In live's rows (spec library-rows) the card fills its cell, its picture covering the box. */
  fill?: boolean | undefined;
  /** Live's drag onto a project tile (spec library-card-drag); none in the trash. */
  drag?: MediaCardDrag | undefined;
}) {
  const rest = scene ?? null;
  // Live's image glyph, as an img named by the file, so a card reads the same with or without a
  // picture: a trashed file has no URLs (S1), and audio has no picture.
  const placeholder = (
    <div className={classes.tile}>
      <img src={glyph} alt={asset.name} className={classes.glyph} />
    </div>
  );
  const picture =
    asset.urls.thumb ??
    (asset.kind === "image" ? asset.urls.original : null) ??
    (asset.kind === "video" ? asset.urls.poster : null);
  return (
    <MediaCard
      still
      drag={drag}
      menu={actions}
      selected={selection?.checked === true}
      corner={
        selection === undefined ? undefined : (
          <Checkbox
            radius="xl"
            size="md"
            // Checked, a filled accent badge with a white edge, readable on any picture.
            classNames={{
              root: classes.select,
              input: classes.selectInput,
              icon: classes.selectIcon,
            }}
            aria-label={`Select ${asset.name}`}
            checked={selection.checked}
            onChange={selection.onToggle}
          />
        )
      }
    >
      {/* One child, so the card's hover zoom moves the picture and its length together. */}
      <Frame asset={asset} onOpen={onOpen} fill={fill}>
        {asset.kind === "video" && asset.urls.original !== null ? (
          // Live's scrubber: the pointer picks the time, and held still it plays.
          <VideoScrubber
            src={asset.urls.original}
            poster={rest === null ? picture : rest.thumbnail_url}
            durationMs={asset.duration_ms}
            thumbnailSeconds={rest === null ? null : rest.thumbnail_seconds}
            label={asset.name}
            fallback={placeholder}
          />
        ) : picture !== null ? (
          // A picture the bucket cannot give (a brand import's file it was refused) shows the
          // glyph's tile, as a file with none.
          <CardPicture src={picture} alt={asset.name} fallback={placeholder} />
        ) : (
          placeholder
        )}
        {indexed && asset.index_status === "pending" ? (
          // Ours (spec S3): live shows no index state; Re-index is how a member asks again.
          <span className={classes.indexState}>
            <Loader size={10} color="var(--cai-color-on-overlay)" aria-hidden />
            <span className={classes.indexWords}>Indexing for search</span>
          </span>
        ) : indexed && asset.index_status === "failed" ? (
          <span className={classes.indexState}>
            <SearchX size={12} aria-hidden />
            <span className={classes.indexWords}>Not in search</span>
          </span>
        ) : null}
        {asset.kind === "video" && asset.urls.original === null && asset.duration_ms !== null ? (
          // A video it cannot play: its still and its length, as the scrubber shows it at rest.
          <span className={classes.duration}>{scrubTime(asset.duration_ms / 1000)}</span>
        ) : null}
      </Frame>
    </MediaCard>
  );
}

/** The picture's frame: a button that opens the preview, or a plain box where nothing opens. */
function Frame({
  asset,
  onOpen,
  fill,
  children,
}: {
  asset: Asset;
  onOpen: (() => void) | undefined;
  fill: boolean;
  children: ReactNode;
}) {
  // At the file's shape in the grid; the cell's own box in a row.
  const box = fill ? { height: "100%" } : shapeStyle(asset);
  if (onOpen === undefined) {
    return (
      <div className={classes.frame} style={box}>
        {children}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={`${classes.frame} ${classes.opens}`}
      style={box}
      aria-label={`Preview ${asset.name}`}
      onClick={onOpen}
    >
      {children}
    </button>
  );
}
